"use client";

import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo } from "react";
import { Control, Controller, useFormContext, useWatch } from "react-hook-form";

import FormAutoComplete from "@/components/common/forms/FormAutoComplete";
import FormAutoCompleteFullScreen from "@/components/common/forms/FormAutoCompleteFullScreen";
import FormSelect from "@/components/common/forms/FormSelect";
import FormTextField from "@/components/common/forms/FormTextField";
import TextArea from "@/components/ui/TextArea";
import { useUAECarFormData } from "@/hooks/useFormOptions";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getDealDriveBrandsPaginated } from "@/lib/api/get-dealdrive-brands";
import { getDealDriveModelVersionsPaginated } from "@/lib/api/get-dealdrive-model-versions";
import { getDealDriveModelsPaginated } from "@/lib/api/get-dealdrive-models";
import { getDealDriveSpecsPaginated } from "@/lib/api/get-dealdrive-specs";
import { SecondStepSchemaType } from "@/schemas/validation/sellCarFormSchema";

interface CarDetailsStepProps {
  control: Control<SecondStepSchemaType>;
}

const CarDetailsStep = ({ control }: CarDetailsStepProps) => {
  const t = useTranslations("SellCar.carDetails");
  const tForm = useTranslations("SellCar.form");
  const { setValue, getValues } = useFormContext<SecondStepSchemaType>();
  const isSmallerDevice = useMediaQuery("(max-width: 768px)");

  // Use useWatch to reactively track form values
  const brand = useWatch({ control, name: "brand" });
  const model = useWatch({ control, name: "model" });

  const { yearOptions } = useUAECarFormData();

  // Memoize options to prevent unnecessary re-renders
  const tradeInOptions = useMemo(
    () => [
      { value: "yes", label: t("tradeIn.yes") },
      { value: "no", label: t("tradeIn.no") },
    ],
    [t],
  );

  // Restore initial values from form state if present
  useEffect(() => {
    // This effect ensures names are preserved when the component mounts
    const values = getValues();
    if (values.brand && !values.brandName) {
      // If we have an ID but not a name, try to fetch the name
      getDealDriveBrandsPaginated("", 1).then((response) => {
        const brandOption = response.data.find((item) => item.value === values.brand);
        if (brandOption) {
          setValue("brandName", brandOption.label);
        }
      });
    }

    if (values.model && !values.modelName && values.brand) {
      getDealDriveModelsPaginated("", 1, { brandId: values.brand }).then((response) => {
        const modelOption = response.data.find((item) => item.value === values.model);
        if (modelOption) {
          setValue("modelName", modelOption.label);
        }
      });
    }

    if (values.modelVersion && !values.modelVersionName && values.brand && values.model) {
      getDealDriveModelVersionsPaginated("", 1, {
        brandId: values.brand,
        modelId: values.model,
      }).then((response) => {
        const versionOption = response.data.find((item) => item.value === values.modelVersion);
        if (versionOption) {
          setValue("modelVersionName", versionOption.label);
        }
      });
    }

    if (values.specification && !values.specificationName) {
      getDealDriveSpecsPaginated("", 1).then((response) => {
        const specOption = response.data.find((item) => item.value === values.specification);
        if (specOption) {
          setValue("specificationName", specOption.label);
        }
      });
    }
  }, []);

  const loadMakesOptions = useCallback(async (inputValue: string, page?: number) => {
    try {
      const response = await getDealDriveBrandsPaginated(inputValue, page);
      return response;
    } catch (error) {
      console.error("Error loading makes:", error);
      return {
        data: [],
        meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
      };
    }
  }, []);

  const loadModelsOptions = useCallback(
    async (inputValue: string, page?: number) => {
      if (!brand) {
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
      try {
        const response = await getDealDriveModelsPaginated(inputValue, page, { brandId: brand });
        return response;
      } catch (error) {
        console.error("Error loading models:", error);
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
    },
    [brand],
  );

  const loadModelVersionsOptions = useCallback(
    async (inputValue: string, page?: number) => {
      if (!brand || !model) {
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
      try {
        const response = await getDealDriveModelVersionsPaginated(inputValue, page, {
          brandId: brand,
          modelId: model,
        });
        return response;
      } catch (error) {
        console.error("Error loading model versions:", error);
        return {
          data: [],
          meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
        };
      }
    },
    [brand, model],
  );

  const loadSpecificationsOptions = useCallback(async (inputValue: string, page?: number) => {
    try {
      const response = await getDealDriveSpecsPaginated(inputValue, page);
      return response;
    } catch (error) {
      console.error("Error loading specifications:", error);
      return {
        data: [],
        meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
      };
    }
  }, []);

  return (
    <div className="mt-10">
      <div className="mt-4">
        <div className="mt-10">
          <h4 className="mb-4 font-inter text-base font-bold text-primary-foreground">{t("title")}</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isSmallerDevice ? (
              <FormAutoCompleteFullScreen
                isClearable
                name="brand"
                control={control}
                placeholder={t("make")}
                helperText={tForm("chooseMake")}
                loadOptions={loadMakesOptions}
                onSelect={(option) => {
                  if (option) {
                    setValue("brand", option.value);
                    setValue("brandName", option.label);
                    // Clear dependent fields when brand changes
                    setValue("model", "");
                    setValue("modelName", "");
                    setValue("modelVersion", "");
                    setValue("modelVersionName", "");
                  }
                }}
                defaultOptions={true}
              />
            ) : (
              <FormAutoComplete
                name="brand"
                control={control}
                placeholder={t("make")}
                loadOptions={loadMakesOptions}
                onSelect={(option) => {
                  if (option) {
                    setValue("brand", option.value);
                    setValue("brandName", option.label);
                    // Clear dependent fields when brand changes
                    setValue("model", "");
                    setValue("modelName", "");
                    setValue("modelVersion", "");
                    setValue("modelVersionName", "");
                  }
                }}
                defaultOptions={true}
              />
            )}
            {isSmallerDevice
              ? brand && (
                  <FormAutoCompleteFullScreen
                    name="model"
                    control={control}
                    placeholder={t("model")}
                    loadOptions={loadModelsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("model", option.value);
                        setValue("modelName", option.label);
                        // Clear version when model changes
                        setValue("modelVersion", "");
                        setValue("modelVersionName", "");
                      }
                    }}
                    dependencies={{ brand }}
                    defaultOptions={!!brand}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={() => (brand ? t("noModelsFound") : t("selectMakeFirst"))}
                  />
                )
              : brand && (
                  <FormAutoComplete
                    name="model"
                    control={control}
                    placeholder={t("model")}
                    loadOptions={loadModelsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("model", option.value);
                        setValue("modelName", option.label);
                        // Clear version when model changes
                        setValue("modelVersion", "");
                        setValue("modelVersionName", "");
                      }
                    }}
                    dependencies={{ brand }}
                    defaultOptions={!!brand}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={() => (brand ? t("noModelsFound") : t("selectMakeFirst"))}
                  />
                )}

            {isSmallerDevice
              ? brand &&
                model && (
                  <FormAutoCompleteFullScreen
                    name="modelVersion"
                    control={control}
                    placeholder={t("trim")}
                    loadOptions={loadModelVersionsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("modelVersion", option.value);
                        setValue("modelVersionName", option.label);
                      }
                    }}
                    dependencies={{ brand, model }}
                    defaultOptions={!!(brand && model)}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={() => {
                      if (!brand) return t("selectMakeFirst");
                      if (!model) return t("selectModelFirst");
                      return t("noVersionsFound");
                    }}
                    hideIfNoData
                  />
                )
              : brand &&
                model && (
                  <FormAutoComplete
                    name="modelVersion"
                    control={control}
                    placeholder={t("trim")}
                    loadOptions={loadModelVersionsOptions}
                    onSelect={(option) => {
                      if (option) {
                        setValue("modelVersion", option.value);
                        setValue("modelVersionName", option.label);
                      }
                    }}
                    dependencies={{ brand, model }}
                    defaultOptions={!!(brand && model)}
                    shouldResetOnDependencyChange
                    customNoOptionsMessage={() => {
                      if (!brand) return t("selectMakeFirst");
                      if (!model) return t("selectModelFirst");
                      return t("noVersionsFound");
                    }}
                    hideIfNoData
                  />
                )}

            <FormSelect<SecondStepSchemaType>
              name="year"
              control={control}
              placeholder={t("year")}
              options={yearOptions}
            />

            <FormTextField
              name="mileage"
              className="min-h-[58px] placeholder:text-base placeholder:font-normal"
              control={control}
              placeholder={t("mileage")}
              type="number"
              formatNumber
            />

            <FormAutoComplete
              name="specification"
              control={control}
              placeholder={t("specifications")}
              loadOptions={loadSpecificationsOptions}
              onSelect={(option) => {
                if (option) {
                  setValue("specification", option.value);
                  setValue("specificationName", option.label);
                }
              }}
              defaultOptions={true}
            />

            <div className="md:col-span-2 lg:col-span-3">
              <FormSelect
                name="tradeIn"
                control={control}
                placeholder={t("tradeIn.placeholder")}
                options={tradeInOptions}
              />
            </div>
          </div>

          <div className="mt-4 lg:col-span-3">
            <Controller
              name="description"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextArea
                    rows={5}
                    placeholder={t("notes")}
                    className="inter w-full bg-secondary-light text-base placeholder:text-base placeholder:font-normal placeholder:text-secondary-foreground"
                    {...field}
                  />
                  {error && <p className="inter text-sm font-semibold text-red-500 rtl:text-right">{error.message}</p>}
                </>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetailsStep;
